import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {FmlxIcon, FmlxRadioButton} from 'fmlx-common-ui';

import FastModal from '../../components/FastModal';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {hideLicenseModal} from '../../stores/actions/licenceStateAction';
import {hideLoading, showLoading, showSnackbar} from '../../stores/actions/appStateAction';
import withIntl from '../../lib/withIntl';
import {
  activateLicense,
  activateTrialLicense,
  getLicenseData,
  importLicense,
} from '../../stores/business/licenseBusiness';
import {FastRobotErrorcode, Feedback, LicenseStatus} from '../../constants/dataEnum';
import {setErrorCodeNumber} from '../../stores/business/commonBusiness';
import {clearModalError} from '../../stores/actions/modalStateAction';

const mapStateToProps = (state) => ({
  showModal: state.licenseState.showModal,
  licenseStatus: state.licenseState.status,
  modalStateErrorCodeNumber: state.modalState.errorCodeNumber,
});

const mapDispatchToProps = (dispatch) => ({
  licenseStateActionHideLicenseModal: bindActionCreators(hideLicenseModal, dispatch),
  licensBusinessimportLicense: bindActionCreators(importLicense, dispatch),
  licensBusinessactivateLicense: bindActionCreators(activateLicense, dispatch),
  licensBusinessactivateTrialLicense: bindActionCreators(activateTrialLicense, dispatch),
  licensBusinessGetLicenseData: bindActionCreators(getLicenseData, dispatch),
  appStateActionShowSnackbar: bindActionCreators(showSnackbar, dispatch),
  appStateShowLoading: bindActionCreators(showLoading, dispatch),
  appStateHideLoading: bindActionCreators(hideLoading, dispatch),
  commonBusinessSetErrorCodeNumber: bindActionCreators(setErrorCodeNumber, dispatch),
  modalStateActionClearErrorCode: bindActionCreators(clearModalError, dispatch),
});

function ModalLicenseActivation({
  intl,
  showModal,
  licenseStatus,
  licenseStateActionHideLicenseModal,
  licensBusinessimportLicense,
  licensBusinessactivateLicense,
  licensBusinessactivateTrialLicense,
  licensBusinessGetLicenseData,
  appStateActionShowSnackbar,
  appStateShowLoading,
  appStateHideLoading,
  commonBusinessSetErrorCodeNumber,
  modalStateActionClearErrorCode,
  modalStateErrorCodeNumber,
}) {
  const [isPermanentLicense, setIsPermanentLicense] = useState(true);
  const [valid, setValid] = useState(false);
  const [fileName, setFileName] = useState('');
  const [licenseError, setLicenseError] = useState('');
  const [licenseWarning, setLicenseWarning] = useState('');

  useEffect(() => {
    if (!isPermanentLicense) {
      setIsPermanentLicense(true);
      setLicenseError('');
      setFileName('');
      setValid(false);
      setLicenseWarning('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseStatus]);

  const readLicenseFile = (fileList) => {
    const file = fileList[0];
    if (!file) {
      return;
    }
    modalStateActionClearErrorCode();
    appStateShowLoading(intl.formatMessage({id: 'licensing.loadingScreen.uploadingFile'}));
    setFileName(file.name);

    const reader = new FileReader();

    reader.onload = async () => {
      const license = reader.result;
      setLicenseError('');
      setLicenseWarning('');

      const response = await licensBusinessimportLicense(license);

      if (response === FastRobotErrorcode.NoError) {
        setValid(true);
      } else if (response === FastRobotErrorcode.INVALID_LICENSE_MAINTENANCE_EXPIRED) {
        setValid(true);
        setLicenseWarning(intl.formatMessage({id: 'licenseing.modal.licenseActivation.errorMessage.expiredDate'}));
      } else {
        if (response === FastRobotErrorcode.INVALID_LICENSE_FILE) {
          setLicenseError(intl.formatMessage({id: 'licenseing.modal.licenseActivation.errorMessage.invalidFile'}));
        } else if (response === FastRobotErrorcode.INVALID_LICENSE_EXPIRED) {
          setLicenseError(
            intl.formatMessage({id: 'licenseing.modal.licenseActivation.errorMessage.invalidLicenseExpired'}),
          );
        } else if (response === FastRobotErrorcode.INVALID_LICENSE_SERIAL_NUMBER) {
          setLicenseError(intl.formatMessage({id: 'licenseing.modal.licenseActivation.errorMessage.invalidSN'}));
        } else if (response === FastRobotErrorcode.INVALID_LICENSE_PRODUCT_LICENSE) {
          setLicenseError(intl.formatMessage({id: 'licenseing.modal.licenseActivation.errorMessage.wrongProduct'}));
        }

        await commonBusinessSetErrorCodeNumber(response);
        setValid(false);
      }
      appStateHideLoading();
    };

    reader.readAsText(file);
  };

  const activateHandler = async () => {
    appStateShowLoading(intl.formatMessage({id: 'licensing.loadingScreen.activatingLicense'}));

    let response;
    if (isPermanentLicense) {
      response = await licensBusinessactivateLicense();
    } else {
      response = await licensBusinessactivateTrialLicense();
    }

    if (
      response === FastRobotErrorcode.NoError ||
      response === FastRobotErrorcode.INVALID_LICENSE_MAINTENANCE_EXPIRED
    ) {
      // get license data
      const licenseData = await licensBusinessGetLicenseData();
      appStateHideLoading();
      setLicenseError('');
      setFileName('');
      setValid(false);
      setLicenseWarning('');

      if (licenseWarning) {
        appStateActionShowSnackbar(
          Feedback.INFO,
          <FormattedHTMLMessage id="licenseing.notification.licenseHasBeenActivated.withInfo" />,
        );
      } else {
        appStateActionShowSnackbar(
          Feedback.SUCCESS,
          <FormattedHTMLMessage
            id="licenseing.notification.licenseHasBeenActivated.success"
            values={{expiredDate: new Date(licenseData.expiryDate).toLocaleDateString()}}
          />,
        );
      }
      return;
    }
    appStateHideLoading();
  };

  const renderContent = () => (
    <>
      {licenseStatus !== LicenseStatus.NO_LICENSE && (
        <>
          <div>
            <FormattedHTMLMessage id="licensing.modal.liceseActivation.option1.text" />
          </div>
          <div className="upload-file-button" style={{marginTop: 20}}>
            <input
              type="file"
              className="upload-file-button__input"
              id="updater-file"
              accept=".xml"
              onChange={(event) => {
                readLicenseFile(event.target.files);
              }}
            />
            <label htmlFor="updater-file" className="upload-file-button__label">
              <span className="upload-file-button__icon icon-uploadprotocol_contextualmenu" />
              {intl.formatMessage({id: 'about.updaterModal.uploadButton'})}
            </label>
            <span className="upload-file-button__filename">{fileName}</span>
          </div>
          {(licenseError || licenseWarning) && (
            <div
              className={`${licenseError ? 'upload-file-button__error' : 'upload-file-button__warning'}`}
              style={{marginTop: 10}}
            >
              <FmlxIcon name="Warning" size="xs" customColor="red" />
              <span>
                {licenseError}
                {licenseWarning}
              </span>
            </div>
          )}
        </>
      )}

      {licenseStatus === LicenseStatus.NO_LICENSE && (
        <div>
          {intl.formatMessage({id: 'licensing.modal.liceseActivation.text'})}
          <FmlxRadioButton
            size="sm"
            items={[
              {
                content: <FormattedHTMLMessage id="licensing.modal.liceseActivation.option1" />,
                hint: isPermanentLicense ? (
                  <div style={{marginBottom: 10}}>
                    <div style={{color: 'black', fontSize: 14}}>
                      <FormattedHTMLMessage id="licensing.modal.liceseActivation.option1.text" />
                    </div>
                    <div className="upload-file-button" style={{marginTop: 10}}>
                      <input
                        type="file"
                        className="upload-file-button__input"
                        id="updater-file"
                        accept=".xml"
                        onChange={(event) => {
                          readLicenseFile(event.target.files);
                        }}
                      />
                      <label htmlFor="updater-file" className="upload-file-button__label">
                        <span className="upload-file-button__icon icon-uploadprotocol_contextualmenu" />
                        {intl.formatMessage({id: 'about.updaterModal.uploadButton'})}
                      </label>
                      <span className="upload-file-button__filename">{fileName}</span>
                    </div>
                    {(licenseError || licenseWarning) && (
                      <div
                        className={`${licenseError ? 'upload-file-button__error' : 'upload-file-button__warning'}`}
                        style={{marginTop: 10}}
                      >
                        <FmlxIcon name="Warning" size="xs" customColor="red" />
                        <span>
                          {licenseError}
                          {licenseWarning}
                        </span>
                      </div>
                    )}
                  </div>
                ) : null,
                type: 'information',
                disabled: false,
              },
              {
                content: (
                  <div>
                    <div>
                      <FormattedHTMLMessage id="licensing.modal.liceseActivation.option2" />
                    </div>
                  </div>
                ),
                hint: !isPermanentLicense ? (
                  <div style={{fontSize: 14, color: 'black'}}>
                    <FormattedHTMLMessage id="licensing.modal.liceseActivation.option2.text" />
                  </div>
                ) : null,
                type: 'information',
                disabled: false,
              },
            ]}
            onChange={(e) => {
              if (e.index === 0) {
                setIsPermanentLicense(true);
                setValid(!licenseError && !!fileName);
              }
              if (e.index === 1) {
                setIsPermanentLicense(false);
                setValid(true);
              }
            }}
            selectedIndex={0}
          />
        </div>
      )}
    </>
  );

  const btnAction = [
    {
      className: 'btn-lg',
      style: 'primary',
      disabled: !valid,
      handlerClick: () => {
        licenseStateActionHideLicenseModal();
        activateHandler();
      },
      text: intl.formatMessage({id: 'licensing.modal.liceseActivation.firstButton'}),
    },
    {
      className: 'btn-lg',
      style: 'secondary',
      handlerClick: async () => {
        licenseStateActionHideLicenseModal();
        licensBusinessGetLicenseData();
        setValid(false);
        setLicenseError('');
        setLicenseWarning('');
        setFileName('');
        setIsPermanentLicense(true);
        await setTimeout(() => {
          modalStateActionClearErrorCode();
        }, 500);
      },
      text: intl.formatMessage({id: 'licensing.modal.liceseActivation.secondButton'}),
    },
  ];

  return (
    <FastModal
      header={intl.formatMessage({id: 'licensing.modal.liceseActivation.title'})}
      content={renderContent()}
      btnAction={btnAction}
      show={showModal}
      className="license-activation"
      closeDisabled
      errorCodeNumber={modalStateErrorCodeNumber}
      size="md"
    />
  );
}

ModalLicenseActivation.propTypes = {
  intl: PropTypes.any.isRequired,
  showModal: PropTypes.bool,
  licenseStatus: PropTypes.string,
  licenseStateActionHideLicenseModal: PropTypes.func,
  licensBusinessimportLicense: PropTypes.func,
  licensBusinessactivateLicense: PropTypes.func,
  licensBusinessactivateTrialLicense: PropTypes.func,
  licensBusinessGetLicenseData: PropTypes.func,
  appStateActionShowSnackbar: PropTypes.func,
  appStateShowLoading: PropTypes.func,
  appStateHideLoading: PropTypes.func,
  commonBusinessSetErrorCodeNumber: PropTypes.func.isRequired,
  modalStateActionClearErrorCode: PropTypes.func.isRequired,
  modalStateErrorCodeNumber: PropTypes.number.isRequired,
};

ModalLicenseActivation.defaultProps = {
  showModal: false,
  licenseStatus: '',
  licenseStateActionHideLicenseModal: () => {},
  licensBusinessimportLicense: () => {},
  licensBusinessactivateLicense: () => {},
  licensBusinessactivateTrialLicense: () => {},
  licensBusinessGetLicenseData: () => {},
  appStateActionShowSnackbar: () => {},
  appStateShowLoading: () => {},
  appStateHideLoading: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(ModalLicenseActivation));
